import type { HighlighterCore, ThemedToken } from 'shiki/core';

/** Grammars the runtime highlighter knows; anything else renders as plain text. */
export type CodeLanguage = 'json' | 'bash' | 'ts' | 'text';

let highlighter: Promise<HighlighterCore> | undefined;

/**
 * One shiki core for the docs, loaded on first use: two themes, three grammars and the
 * JavaScript regex engine (no wasm). Guides are highlighted at build time; this is for the
 * JSON examples and snippets endpoint pages render at runtime.
 */
function loadHighlighter(): Promise<HighlighterCore> {
  highlighter ??= (async () => {
    const [{ createHighlighterCore }, { createJavaScriptRegexEngine }] = await Promise.all([
      import('shiki/core'),
      import('shiki/engine/javascript'),
    ]);
    return createHighlighterCore({
      themes: [import('shiki/themes/github-light.mjs'), import('shiki/themes/github-dark.mjs')],
      langs: [
        import('shiki/langs/json.mjs'),
        import('shiki/langs/bash.mjs'),
        import('shiki/langs/typescript.mjs'),
      ],
      engine: createJavaScriptRegexEngine(),
    });
  })();
  // A failed chunk load should not stick: the next block tries again.
  highlighter.catch(() => {
    highlighter = undefined;
  });
  return highlighter;
}

/**
 * Tokens per line with both themes as CSS variables (`--shiki-light`, `--shiki-dark`), which
 * `docs.css` maps to the admin theme. Resolves to null for plain text.
 */
export async function highlightLines(
  code: string,
  language: CodeLanguage,
): Promise<ThemedToken[][] | null> {
  if (language === 'text') return null;
  const shiki = await loadHighlighter();
  return shiki.codeToTokens(code, {
    lang: language === 'ts' ? 'typescript' : language,
    themes: { light: 'github-light', dark: 'github-dark' },
    defaultColor: false,
  }).tokens;
}
